import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { 
  Mail, Plus, Trash2, CheckCircle, AlertCircle, Loader2, 
  Copy, RefreshCw, Shield, Eye, EyeOff, X, ExternalLink, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const DKIMRegistration = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [senders, setSenders] = useState([]);
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [showAddSender, setShowAddSender] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [loadingRecords, setLoadingRecords] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);

  const [newDomain, setNewDomain] = useState({ domain: '' });
  const [newSender, setNewSender] = useState({ name: '', email: '', dkim_domain_id: '', is_default: false });

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isSuperAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [domainsRes, sendersRes] = await Promise.all([
        api.get('/dkim/domains'),
        api.get('/dkim/senders')
      ]);
      setDomains(domainsRes.data);
      setSenders(sendersRes.data);
    } catch (error) {
      console.error('Failed to fetch DKIM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.domain) {
      toast.error('Please enter a domain');
      return;
    }

    setSaving(true);
    try {
      await api.post('/dkim/domains', newDomain);
      toast.success('Domain registered! Configure DNS records to verify.');
      setShowAddDomain(false);
      setNewDomain({ domain: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register domain');
    } finally {
      setSaving(false);
    }
  };

  // Toggle the DNS-records panel. On open, pull fresh records from Brevo via the
  // single-domain GET (which self-heals records that weren't captured at creation).
  const handleToggleRecords = async (domain) => {
    if (selectedDomain === domain.id) {
      setSelectedDomain(null);
      return;
    }
    setSelectedDomain(domain.id);
    if (!domain.records || domain.records.length === 0) {
      setLoadingRecords(domain.id);
      try {
        const res = await api.get(`/dkim/domains/${domain.id}`);
        setDomains((prev) => prev.map((d) => (d.id === domain.id ? { ...d, ...res.data } : d)));
      } catch (error) {
        toast.error('Could not load DNS records from Brevo');
      } finally {
        setLoadingRecords(null);
      }
    }
  };

  const handleVerifyDomain = async (domainId) => {
    setVerifying(domainId);
    try {
      const response = await api.post(`/dkim/domains/${domainId}/verify`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(null);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!confirm('Are you sure you want to delete this domain?')) return;
    
    try {
      await api.delete(`/dkim/domains/${domainId}`);
      toast.success('Domain deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete domain');
    }
  };

  const handleAddSender = async (e) => {
    e.preventDefault();
    if (!newSender.name || !newSender.email || !newSender.dkim_domain_id) {
      toast.error('Please fill in all fields');
      return;
    }

    setSaving(true);
    try {
      await api.post('/dkim/senders', newSender);
      toast.success('Email sender created!');
      setShowAddSender(false);
      setNewSender({ name: '', email: '', dkim_domain_id: '', is_default: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sender');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSender = async (senderId) => {
    if (!confirm('Are you sure you want to delete this sender?')) return;
    
    try {
      await api.delete(`/dkim/senders/${senderId}`);
      toast.success('Sender deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sender');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full"><CheckCircle className="w-3 h-3" /> Verified</span>;
      case 'pending':
        return <span className="flex items-center gap-1 text-xs font-medium text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full"><AlertCircle className="w-3 h-3" /> Pending</span>;
      default:
        return <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full"><AlertCircle className="w-3 h-3" /> {status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-outfit font-bold text-slate-900">DKIM Registration</h1>
                <p className="text-sm text-slate-500">Manage email domains and sender addresses for verified email delivery</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Domains Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-outfit font-semibold text-slate-900">Registered Domains</h2>
            <Button onClick={() => setShowAddDomain(true)} className="bg-primary hover:bg-primary-hover">
              <Plus className="w-4 h-4 mr-2" /> Add Domain
            </Button>
          </div>

          {domains.length === 0 ? (
            <Card className="p-8 text-center">
              <Globe className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No domains registered</h3>
              <p className="text-sm text-slate-500 mb-4">Register a domain to start sending emails with DKIM authentication</p>
              <Button onClick={() => setShowAddDomain(true)} variant="outline">
                <Plus className="w-4 h-4 mr-2" /> Register Domain
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {domains.map((domain) => (
                <Card key={domain.id} className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${domain.status === 'verified' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                        <Globe className={`w-5 h-5 ${domain.status === 'verified' ? 'text-green-600' : 'text-yellow-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{domain.domain}</h3>
                        <p className="text-sm text-slate-500">Added {new Date(domain.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(domain.status)}
                      {domain.status !== 'verified' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVerifyDomain(domain.id)}
                          disabled={verifying === domain.id}
                        >
                          {verifying === domain.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <><RefreshCw className="w-4 h-4 mr-1" /> Verify</>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleRecords(domain)}
                      >
                        {selectedDomain === domain.id ? 'Hide Records' : 'DNS Records'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDomain(domain.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* DNS Records */}
                  {selectedDomain === domain.id && (
                    <div className="mt-4 pt-4 border-t">
                      {loadingRecords === domain.id ? (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading DNS records…
                        </div>
                      ) : domain.records && domain.records.length > 0 ? (
                        <>
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Add these DNS records to verify your domain:</h4>
                      <div className="space-y-3">
                        {domain.records.map((record, idx) => (
                          <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-slate-500 uppercase">{record.type} Record</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(record.value)}
                                className="h-6 text-xs"
                              >
                                <Copy className="w-3 h-3 mr-1" /> Copy
                              </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-slate-500">Name:</span>
                                <code className="ml-2 bg-slate-200 px-2 py-0.5 rounded text-xs">{record.name}</code>
                              </div>
                              <div className="col-span-2">
                                <span className="text-slate-500">Value:</span>
                                <code className="ml-2 bg-slate-200 px-2 py-0.5 rounded text-xs break-all">{record.value}</code>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                        </>
                      ) : (
                        <p className="text-sm text-slate-500">
                          No DNS records returned by Brevo for this domain. Try <strong>Verify</strong>, or check the domain in your Brevo dashboard.
                        </p>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Email Senders Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-outfit font-semibold text-slate-900">Email Senders</h2>
            <Button 
              onClick={() => setShowAddSender(true)} 
              variant="outline"
              disabled={domains.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" /> Add Sender
            </Button>
          </div>

          {senders.length === 0 ? (
            <Card className="p-8 text-center">
              <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No email senders</h3>
              <p className="text-sm text-slate-500 mb-4">
                {domains.length === 0 
                  ? 'Register a domain first to create email senders'
                  : 'Create email sender addresses for your campaigns'
                }
              </p>
              {domains.length > 0 && (
                <Button onClick={() => setShowAddSender(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" /> Add Sender
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {senders.map((sender) => (
                <Card key={sender.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${sender.is_verified ? 'bg-green-100' : 'bg-slate-100'}`}>
                        <Mail className={`w-4 h-4 ${sender.is_verified ? 'text-green-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900">{sender.name}</h3>
                          {sender.is_default && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Default</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{sender.email}</p>
                        <p className="text-xs text-slate-400">Domain: {sender.domain}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteSender(sender.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Domain Modal */}
      {showAddDomain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-outfit font-bold">Register DKIM Domain</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddDomain(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <form onSubmit={handleAddDomain} className="p-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Domain Name</Label>
                  <Input
                    value={newDomain.domain}
                    onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                    placeholder="events.example.com"
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    The domain will be registered with Brevo (configured on the server). After registration you'll get DNS records (DKIM, SPF, Brevo verification) to add in your DNS provider.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button type="button" variant="outline" onClick={() => setShowAddDomain(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary-hover">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Register Domain
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Add Sender Modal */}
      {showAddSender && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-outfit font-bold">Add Email Sender</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddSender(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <form onSubmit={handleAddSender} className="p-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Sender Name</Label>
                  <Input
                    value={newSender.name}
                    onChange={(e) => setNewSender({ ...newSender, name: e.target.value })}
                    placeholder="Kaizen Events"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Select Domain</Label>
                  <select
                    value={newSender.dkim_domain_id}
                    onChange={(e) => setNewSender({ ...newSender, dkim_domain_id: e.target.value, email: '' })}
                    className="w-full mt-1 h-10 rounded-lg border border-slate-200 px-3 text-sm"
                  >
                    <option value="">Select a domain...</option>
                    {domains.map((domain) => (
                      <option key={domain.id} value={domain.id}>
                        {domain.domain} {domain.status === 'verified' ? '✓' : '(pending)'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Email Address</Label>
                  <Input
                    type="email"
                    value={newSender.email}
                    onChange={(e) => setNewSender({ ...newSender, email: e.target.value })}
                    placeholder={newSender.dkim_domain_id ? `noreply@${domains.find(d => d.id === newSender.dkim_domain_id)?.domain || 'domain.com'}` : 'Select a domain first'}
                    className="mt-1"
                    disabled={!newSender.dkim_domain_id}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Set as Default</Label>
                    <p className="text-xs text-slate-500">Use this sender by default</p>
                  </div>
                  <Switch
                    checked={newSender.is_default}
                    onCheckedChange={(checked) => setNewSender({ ...newSender, is_default: checked })}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button type="button" variant="outline" onClick={() => setShowAddSender(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary-hover">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Add Sender
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DKIMRegistration;
